import { Component, inject, Input as RouterInput, ViewChild } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { BaseComponent } from '../../shared/base.component';
import { UpdateDealerState } from '../../shared/state-interface';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { DealerComponent } from '../shared/dealer.component';
import { Dealer } from '../shared/dealer.model';
import { DealerService } from '../shared/dealer.service';

@Component({
    selector: 'app-update-dealer',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        SiteHeaderComponent,
        DealerComponent
    ],
    templateUrl: './update-dealer.component.html',
    styleUrl: './update-dealer.component.css'
})
export class UpdateDealerComponent extends BaseComponent {

    @RouterInput() id: number = -1;
    @ViewChild(DealerComponent) dealerComponent!:DealerComponent;

    private dealerService: DealerService = inject(DealerService);

    private static _retrievingDealer = "Retrieving Dealer";
    private static _retrievingDealerError = `${UpdateDealerComponent._retrievingDealer} error:`;
    private static _updatingDealer = "Updating Dealer";
    private static _updatingDealerError = `${UpdateDealerComponent._updatingDealer} error:`;

    private _dealer: Dealer = new Dealer();

    public ngAfterViewInit() {
        const state: UpdateDealerState | undefined = this.stateManager.updateDealerState;

        if (state !== undefined) {
            this._dealer = state.dealer;
            this.dealerComponent.restoreState(state.dealerComponentState);
            super.restoreState(state.baseComponentState);
        } else {
            this.dealerComponent.init();
            this.loadDealer(this.id);
        }
    }

    public ngOnDestroy() {
        const state: UpdateDealerState = {} as UpdateDealerState;
        state.dealer = this._dealer;
        state.dealerComponentState = this.dealerComponent.getState();
        state.baseComponentState = super.getState();
        this.stateManager.updateDealerState = state;
    }

    protected async loadDealer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerService.getDealer(await getIdToken(currentUser), id);
            this.message = "";
            super.showSnackBar(`${UpdateDealerComponent._retrievingDealer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-dealer"] == "success") {
                        // Convert generic JSON object to type of Dealer.
                        this._dealer = Dealer.toDealer(json["dealer"]);
                        this.dealerComponent.populateFormFromModel(this._dealer);
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UpdateDealerComponent._retrievingDealerError} ${(<Error>error).message}`;
                    this.message = message;
                }
                super.dismissSnackBar();
            })
        }
    }

    protected cancel() {
        this.router.navigate(["/view-dealers"], { state: { restore: true } });
    }

    protected async save() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            this._dealer = this.dealerComponent.createModelFromForm(this._dealer);

            const observable = await this.dealerService.updateDealer(await getIdToken(currentUser), this._dealer);
            this.message = "";
            super.showSnackBar(`${UpdateDealerComponent._updatingDealer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["update-dealer"] == "success") {
                        this.stateManager.clearDealerNames();
                        const queryParam = `Dealer '${this._dealer.name}' updated.`;
                        this.router.navigate(["/view-dealers"], { queryParams: {"overrideSnackBar": queryParam}, state: { refresh: true } });
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UpdateDealerComponent._updatingDealerError} ${(<Error>error).message}`;
                    this.message = message;
                    super.dismissSnackBar();
                }
            })
        } else {
            const message = `${UpdateDealerComponent._updatingDealerError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }
}